import type { SpecificSection } from '../schemas';
import type { CssResolvers, CssVars } from '../../css-vars';

import { blockWidthVar } from '../../block/styles/block.css-vars';
import { colorValueVar, flexAlignVar, numberWithUnitVar, spacingVars, textAlignVar } from '../../mixins';

/**
 * CSS custom properties + gating classes for the specific (header/footer)
 * section — the replacement for the former JSS `getStyles`. The static rules
 * live in `./section.base.less`; only the per-section values travel through
 * here, so the editor and the published page compute the same bag.
 *
 * **Colour and image resolution are injected.** Theme colours resolve through
 * `@sites/site-theme` and a picked file through `@components/sp-file-manager`, both
 * Angular libraries; entity-core stays framework-free, so the host passes its
 * resolvers in. The image one also carries the app's own CDN base — the editor
 * and the SSR renderer have different ones, which is why it cannot be bound
 * once inside a shared wrapper.
 */
export type SectionBackgroundImage = SpecificSection['props']['background']['image'];
export type SectionColorValue = SpecificSection['props']['background']['color'];
export type SectionLength = SpecificSection['props']['columnGap'];

/**
 * The slice of the parent block these builders read: its width, the alignment it
 * hands its collapsed menu, and whether it draws separators between rows.
 *
 * Structural on purpose — the section is laid out inside the block and follows
 * it, but neither entity should have to know the other's whole shape.
 */
export type SectionParentBlockLike = {
    children: { id: string }[];
    props?: {
        width?: { value: number; unit: string } | null;
        horizontalPosition?: string;
        hasSeparator?: boolean;
    };
};

/**
 * One function per variable, so a host that reads its fields from a signal form
 * can bind them one by one and only rewrite the property that actually changed.
 * The bag builders below compose these, which keeps the two shapes from drifting.
 */

/** A picked image layered over the colour, or the bare colour when there is none. */
export function sectionBgVar(
    color: SectionColorValue,
    backgroundImage: SectionBackgroundImage,
    { colorCss, imageCss }: CssResolvers
): string {
    const background = colorValueVar(color, colorCss);

    return backgroundImage ? `${imageCss(backgroundImage)}, ${background}` : background;
}

/**
 * Position, repeat and size only carry a value once there is an image — with no
 * image they stay unset, leaving the `background` shorthand's own defaults in
 * place (the JSS original set them only in the image branch too).
 */
export function sectionBgLayerVar(backgroundImage: SectionBackgroundImage, value: unknown): string | undefined {
    return backgroundImage ? String(value ?? '') : undefined;
}

/**
 * The row follows the parent block's width, including its default — which is why
 * this delegates rather than repeating the content column's size.
 */
export function sectionMaxWidthVar(width?: { value: number; unit: string } | null): string {
    return blockWidthVar(width as never);
}

/** Half a gap — it is split between the two sides it separates. */
export function sectionGapHalfVar(columnGap?: SectionLength): string {
    return numberWithUnitVar({ value: (columnGap?.value ?? 0) / 2, unit: columnGap?.unit ?? 'px' }, '0px');
}

/** Background of the section row, as one bag. */
export function getSpecificSectionWrapCssVars(section: SpecificSection, resolvers: CssResolvers): CssVars {
    const { background } = section.props;
    const image = background.image;

    const vars: CssVars = {
        '--specific-section-bg': sectionBgVar(background.color, image, resolvers),
    };

    if (!image) {
        return vars;
    }

    // Past the guard above the image is present, so these always carry a value.
    vars['--specific-section-bg-position'] = sectionBgLayerVar(image, background.position) as string;
    vars['--specific-section-bg-repeat'] = sectionBgLayerVar(image, background.repeat) as string;
    vars['--specific-section-bg-size'] = sectionBgLayerVar(image, background.size) as string;

    return vars;
}

/**
 * Layout of the row itself, as one bag. `maxWidth` and the mobile text alignment
 * come from the parent block — the section renders inside it and follows its
 * width mode.
 */
export function getSpecificSectionInnerCssVars(section: SpecificSection, block: SectionParentBlockLike): CssVars {
    const { props } = section;
    const padding = props.padding;

    return {
        '--specific-section-max-width': sectionMaxWidthVar(block.props?.width),
        '--specific-section-align-items': flexAlignVar(props.verticalPosition),
        '--specific-section-gap': numberWithUnitVar(props.columnGap, '0px'),
        '--specific-section-gap-half': sectionGapHalfVar(props.columnGap),
        '--specific-section-slot-margin-half': sectionGapHalfVar(props.slotVerticalMargin),
        ...spacingVars(padding, 'specific-section-padding'),
        '--specific-section-mobile-text-align': textAlignVar(block.props?.horizontalPosition),
    };
}

/**
 * Gating classes for the rules that used to be emitted conditionally.
 *
 * `isConstructor` keeps the editor showing every header slot even when it is
 * hidden on the published page — the user still has to be able to select it.
 */
export function getSpecificSectionInnerCssClasses(
    section: SpecificSection,
    block: SectionParentBlockLike,
    isConstructor = false
): string {
    const classes: string[] = [];

    const sectionIndex = block.children.findIndex((item) => item.id === section.id);
    const isNotLastSection = sectionIndex !== block.children.length - 1;

    if (isNotLastSection && block.props?.hasSeparator) {
        classes.push('has-separator');
    }

    if (!isConstructor) {
        const { slots } = section.props;

        if (slots.left === false) classes.push('hide-left-column');
        if (slots.center === false) classes.push('hide-center-column');
        if (slots.right === false) classes.push('hide-right-column');
    }

    return classes.join(' ');
}
